import { Injectable } from '@angular/core';
import { StorageOperation } from '../../../model/src/lib/storage-operation.model';
import { StoragePropertyOperation } from '../../../model/src/lib/storage-property-operation.model';
import { UserProfile } from '../../../model/src/public-api';
import { isNullOrEmpty } from './../../../util/src/lib/is-null-or-empty';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  readonly keys = {
    IdToken: '________iDt_____',
    UserProfile: '___upF_______',
    lang: '____CurreNtLAng____',
    InvalidGrantCount: 'invalid_grant_count',
    RefreshTokenCount: 'refresh_token_count',
  };
  constructor() {}

  private setItem(storage: Storage, key: string, value: any): void {
    if (storage && key) {
      storage.setItem(key, JSON.stringify(value));
    }
  }

  private getItem<T>(storage: Storage, key: string): T | null {
    if (storage && key) {
      const value = storage.getItem(key);
      if (isNullOrEmpty(value)) {
        return null;
      } else {
        return JSON.parse(value!) as T;
      }
    }

    return null;
  }

  private removeItem(storage: Storage, key: string): void {
    if (storage && key) {
      storage.removeItem(key);
    }
  }

  private clear(storage: Storage): void {
    if (storage) {
      storage.clear();
    }
  }

  private getStorageOperators(storage: Storage): StorageOperation {
    return {
      setItem: (key: string, value: any) => {
        this.setItem(storage, key, value);
      },
      getItem: (key: string) => {
        return this.getItem(storage, key) as any;
      },
      removeItem: (key: string) => {
        this.removeItem(storage, key);
      },
      clear: () => {
        this.clear(storage);
      },
    };
  }

  private getPropertyOperators<T>(
    storage: Storage,
    key: string
  ): StoragePropertyOperation<T> {
    return {
      setItem: (value: any) => {
        this.setItem(storage, key, value);
      },
      getItem: () => {
        return this.getItem<T>(storage, key) as T;
      },
      removeItem: () => {
        this.removeItem(storage, key);
      },
    };
  }

  /**
   * 提供操作 localStorage 的方法
   */
  get local(): StorageOperation {
    return this.getStorageOperators(window.localStorage);
  }

  /**
   * 提供操作 sessionStorage 的方法
   */
  get session(): StorageOperation {
    return this.getStorageOperators(window.sessionStorage);
  }

  /**
   * 提供操作 IdToken 在 storage 存取的方法
   */
  get idToken(): StoragePropertyOperation<string> {
    return this.getPropertyOperators<string>(
      window.localStorage,
      this.keys.IdToken
    );
  }

  /**
   * 提供操作 UserProfile 在 storage 存取的方法
   */
  get userProfile(): StoragePropertyOperation<UserProfile> {
    return this.getPropertyOperators<UserProfile>(
      window.localStorage,
      this.keys.UserProfile
    );
  }

  /**
   * 提供操作 lang 在 storage 存取的方法
   */
  get lang(): StoragePropertyOperation<string> {
    return this.getPropertyOperators<string>(
      window.localStorage,
      this.keys.lang
    );
  }

  /**
   * 提供操作 InvalidGrantCount 在 storage 存取的方法
   */
  get invalidGrantCount(): StoragePropertyOperation<number> {
    return this.getPropertyOperators<number>(
      window.localStorage,
      this.keys.InvalidGrantCount
    );
  }

  /**
   * 提供操作 RefreshTokenCount 在 storage 存取的方法
   */
  get refreshCount(): StoragePropertyOperation<number> {
    return this.getPropertyOperators<number>(
      window.localStorage,
      this.keys.RefreshTokenCount
    );
  }
}
